/**
 * FollowersSubTabs Component
 * Sub-tab navigation within the Followers tab: Followers, Following.
 * Matches the Figma "Elements / Subtab / Community / Members (Admin)" pattern.
 */

import { Search, X } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FollowersTabContent } from "@/features/profile/components/FollowersTabContent"
import type { ProfileStatistics } from "@/features/profile/types/profile.types"
import { useTranslation } from "@/language"
import { cn } from "@/lib"
import type { FollowersSubTab } from "../types/my-rituality.types"
import { FollowingTabContent } from "./FollowingTabContent"

interface FollowersSubTabsProps {
  userId: string
  statistics?: ProfileStatistics | null
  searchQuery?: string
  initialSubTab?: FollowersSubTab
}

export function FollowersSubTabs({
  userId,
  statistics,
  searchQuery,
  initialSubTab,
}: FollowersSubTabsProps) {
  const { t } = useTranslation("myRituality")
  const navigate = useNavigate()
  const [, setSearchParams] = useSearchParams()
  const [activeSubTab, setActiveSubTab] = useState<FollowersSubTab>(initialSubTab ?? "followers")
  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")

  useEffect(() => {
    if (initialSubTab) {
      setActiveSubTab(initialSubTab)
    }
  }, [initialSubTab])

  const followersCount = statistics?.followersCount ?? 0
  const followingCount = statistics?.followingCount ?? 0

  const subTabs: { key: FollowersSubTab; label: string; count: number }[] = [
    { key: "followers", label: t("followersSubTabs.followers"), count: followersCount },
    { key: "following", label: t("followersSubTabs.following"), count: followingCount },
  ]

  const handleInviteFriends = () => {
    navigate("/settings?section=invite-friends")
  }

  return (
    <div>
      <div className="flex items-center justify-between px-5 md:px-0">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-1.25 md:gap-3.75">
          <div className="flex justify-between items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              {t("tabs.followers")}
            </div>
            <div className="md:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder={t("tabs.search")}
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label={t("tabs.closeSearch")}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => setShowSearch(true)}
                  aria-label={t("tabs.search")}
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>

          <div className="w-px h-7.5 bg-text-primary/10 hidden md:block" />

          <div className="flex items-center h-7.5 gap-3.75 md:gap-5 text-sm text-white overflow-x-auto scrollbar-hide">
            {subTabs.map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveSubTab(tab.key)
                  setSearchParams(prev => {
                    const newParams = new URLSearchParams(prev)
                    newParams.set("subTab", tab.key)
                    return newParams
                  })
                }}
                className={cn(
                  "text-sm cursor-pointer transition-opacity whitespace-nowrap shrink-0",
                  activeSubTab === tab.key ? "font-semibold opacity-100" : "font-normal opacity-70",
                )}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleInviteFriends}
          className="hidden md:block h-[50px] w-[220px] rounded-full bg-text-primary text-my-rituality-bg text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
        >
          {t("followersSubTabs.inviteFriends")}
        </button>
      </div>

      <button
        type="button"
        onClick={handleInviteFriends}
        className="block md:hidden h-[50px] my-5 w-full md:w-[220px] rounded-full bg-text-primary text-my-rituality-bg text-[14px] font-semibold uppercase cursor-pointer hover:opacity-90 transition-opacity"
      >
        {t("followersSubTabs.inviteFriends")}
      </button>

      {/* Content */}
      <div className="mt-4">
        {activeSubTab === "followers" && (
          <FollowersTabContent userId={userId} searchQuery={searchQuery} showHeader={false} />
        )}

        {activeSubTab === "following" && (
          <FollowingTabContent userId={userId} searchQuery={searchQuery} />
        )}
      </div>
    </div>
  )
}
